/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `Proposal` for the Caintra Connect frontend.
 * @module components/ProposalsListView/Proposal
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import styles from "./index.module.css";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import StarIcon from "@mui/icons-material/Star";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import { IconButton, Backdrop, CircularProgress } from "@mui/material";
import { getCompanyByRFC } from "../../logic/company/index.js";
import AssetImage from "../AssetImage";

const Proposal = ({
  proposal,
  user,
  company: companyProp,
  onProposalAction,
  onViewProposal,
  sectionType,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [companyFetched, setCompanyFetched] = useState(null);
  const [isLoadingCompany, setIsLoadingCompany] = useState(false);
  const isAccepted = sectionType === "accepted";
  const isRejected = sectionType === "rejected";
  const company = companyProp ?? companyFetched;

  useEffect(() => {
    if (companyProp) {
      return;
    }
    const fetchCompanyData = async () => {
      if (user?.RFC && !companyFetched && !isLoadingCompany) {
        setIsLoadingCompany(true);
        try {
          const companyData = await getCompanyByRFC(user.RFC, navigate);
          if (companyData) {
            setCompanyFetched(companyData);
          }
        } catch (error) {
          logger.error("Error fetching company data:", {
            err: sanitizeError(error),
          });
        } finally {
          setIsLoadingCompany(false);
        }
      }
    };
    fetchCompanyData();
  }, [companyProp, user?.RFC, companyFetched, isLoadingCompany, navigate]);

  const handleProposalClick = (e) => {
    // Check if the click was on a button or its children
    if (e.target.closest(".MuiIconButton-root")) {
      return;
    }
    onViewProposal(proposal);
  };

  return (
    <div
      className={styles.proposalContainer}
      onClick={handleProposalClick}
      style={{ cursor: "pointer" }}
    >
      <div className={styles.proposal}>
        <div className={styles.userInfoContainer}>
          {proposal.esDestacado ? (
            <StarIcon
              className={styles.starIcon}
              onClick={(e) => {
                e.stopPropagation();
                onProposalAction(proposal._id, "unstar");
              }}
            />
          ) : (
            <StarOutlineIcon
              className={styles.starIcon}
              onClick={(e) => {
                e.stopPropagation();
                onProposalAction(proposal._id, "star");
              }}
            />
          )}
          {user?.imagenDePerfil ? (
            <AssetImage
              src={user.imagenDePerfil}
              alt={t("common.avatarAlt")}
              className={styles.userAvatar}
            />
          ) : user?.nombre ? (
            <div className={styles.userAvatar}>{user.nombre.charAt(0)}</div>
          ) : (
            <div className={styles.userAvatar}></div>
          )}
          <div className={styles.userInfoText}>
            {/* <div className={styles.userNameContainer}>
            </div> */}
            <span className={styles.userName}>
              {user?.nombre || t("common.userNotFound")}
            </span>
            <span className={styles.userEmail}>
              ({user?.correoElectronico || ""})
            </span>
            <div className={styles.companyName}>
              {company?.nombreComercial || t("common.companyNotFound")}
            </div>
          </div>
        </div>
      </div>
      <div className={styles.proposalCell}>
        <div className={styles.actionButtonsContainer}>
          <IconButton
            color="success"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onProposalAction(proposal._id, "approve");
            }}
            disabled={isAccepted}
            sx={{
              "& .MuiSvgIcon-root": {
                color: isAccepted ? "#B3E6C3" : "#00E864",
              },
              "&.Mui-disabled": {
                color: "#B3E6C3",
              },
            }}
          >
            <CheckCircleIcon />
          </IconButton>
          <IconButton
            color="error"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onProposalAction(proposal._id, "reject");
            }}
            disabled={isRejected}
            sx={{
              "& .MuiSvgIcon-root": {
                color: isRejected ? "#F7B3B3" : "#EF183B",
              },
              "&.Mui-disabled": {
                color: "#F7B3B3",
              },
            }}
          >
            <CancelIcon />
          </IconButton>
        </div>
      </div>

      {!companyProp && (
        <Backdrop
          sx={{
            color: "#fff",
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
          }}
          open={isLoadingCompany}
        >
          <CircularProgress
            size={60}
            thickness={4}
            sx={{
              color: "#4eabe7",
            }}
          />
        </Backdrop>
      )}
    </div>
  );
};

export default Proposal;
